import {
    createAction,
    createAsyncThunk,
    createSlice,
    isAnyOf,
} from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';

import api, { deleteReq, getReq, postReq, putReq } from 'api';
import { decrementResponse } from 'store/auth';
import { revalidatePaths } from 'utils/revalidate';
import { alertPath } from 'utils/alert-url';
import parseJwt from 'utils/parse-jwt';
import { MAX_PAGE_SIZE } from '../data/globalConstants';

const hydrate = createAction(HYDRATE);

export const fetchAlerts = createAsyncThunk(
    'fetchAlerts',
    async (id, { getState, rejectWithValue }) => {
        try {
            const profileId = getState().company.data.id || id;
            let composedRequest = `/profiles/${profileId}/alerts`;

            if (getState().auth.isAdmin) {
                composedRequest = `/alerts`;
            }

            if (getState().auth.isJournalist) {
                // The API refuses the journalist feed of a profile they are not on.
                // By id: the token's slug goes stale when the profile is renamed.
                const { auth } = getState();
                const ownId = parseJwt(auth.token).companyId;
                if (
                    ownId &&
                    ownId !== profileId &&
                    !auth.isAdmin &&
                    !auth.isFTEditor
                ) {
                    return [];
                }
                composedRequest = `/profiles/${profileId}/alerts-journalists`;
            }

            if (getState().auth.isFTEditor) {
                composedRequest = `/alerts`;
            }

            if (!getState().auth.isAuthenticated) {
                return [];
                // composedRequest = `/alerts`;
            }

            const res = await getReq(composedRequest);

            return res.data.data.items
                .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
                .slice(-100);
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchAlertBySlug = createAsyncThunk(
    'fetchAlertBySlug',
    async ({ slug, author }, { rejectWithValue }) => {
        try {
            const res = await getReq(
                `/alerts/slug/${slug}${author === '0' ? '?author=0' : ''}`
            );
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchResponses = createAsyncThunk(
    'fetchResponses',
    async (id, { rejectWithValue }) => {
        try {
            const res = await getReq(
                `/alerts/${id}/responses?size=${MAX_PAGE_SIZE}`
            );
            return res.data.data.items;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const addAlert = createAsyncThunk(
    'addAlert',
    async (alertDto, { rejectWithValue }) => {
        try {
            // The axios instance directly: postReq drops the response, and with it
            // the API's message (source page off the profile site, slug taken).
            const res = await api.post(`/alerts`, alertDto);
            revalidatePaths([alertPath(res.data.data.slug)]);
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateAlert = createAsyncThunk(
    'updateAlert',
    async (alertDto, { rejectWithValue }) => {
        try {
            const res = await api.put(`/alerts/${alertDto.id}`, alertDto);
            // alertDto.slug is the address before the save, res...slug the one
            // after: when an admin moved the alert, the old path must re-render
            // as a redirect and the new one as the page.
            revalidatePaths([
                alertPath(alertDto.slug),
                alertPath(res.data.data.slug),
            ]);
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const closeAlert = createAsyncThunk(
    'closeAlert',
    async (id, { rejectWithValue }) => {
        try {
            const res = await postReq(`/alerts/${id}/close-alert`);
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const removeAlert = createAsyncThunk(
    'removeAlert',
    async (id, { rejectWithValue }) => {
        try {
            await deleteReq(`/alerts/${id}`);
            return id;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const addAlertResponse = createAsyncThunk(
    'addAlertResponse',
    async ({ id, data }, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState();
            const companyPlan = state.company.data.plan;
            const userResponseCredit = state.auth.responseCredit;

            // Not postReq: it rethrows as a bare Error, and the refusal's body (FREE_COVERAGE_REACHED)
            // is what AddResponse renders.
            const res = await api.post(
                `profiles/${getState().company.data.id}/alerts/${id}/responses`,
                data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            revalidatePaths([alertPath(state.alerts.alert?.slug)]);

            dispatch(addResponse(id));

            // If user has no plan responses left but has response credits, decrement the credit;
            // an answer to the profile's own alert costs none
            const ownAlert =
                state.alerts.alert?.profileId === state.company.data.id;
            if (
                companyPlan?.responsesLeft === 0 &&
                userResponseCredit > 0 &&
                !ownAlert
            ) {
                dispatch(decrementResponse());
            }

            // The API flags the Story Source only when it lists responses; a new one from the
            // expert who raised the alert is theirs too, so it leads without a reload.
            const seededBy = state.alerts.alert?.seededBy;
            return {
                ...res.data.data,
                isStorySource:
                    !!seededBy && seededBy.slug === state.company.data.slug,
            };
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const removeAlertResponse = createAsyncThunk(
    'removeAlertResponse',
    async ({ alertId, responseId }, { rejectWithValue }) => {
        try {
            await deleteReq(`/alerts/${alertId}/responses/${responseId}`);
            return responseId;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateAlertResponse = createAsyncThunk(
    'updateAlertResponse',
    async (
        { alertId, responseId, ...responseDto },
        { getState, rejectWithValue }
    ) => {
        const reqUrl = getState().auth.isAdmin
            ? `/alerts/${alertId}/responses/${responseId}`
            : `/profiles/${
                  getState().company.data.id
              }/alerts/${alertId}/responses/${responseId}`;
        try {
            const res = await putReq(reqUrl, responseDto);
            revalidatePaths([alertPath(getState().alerts.alert?.slug)]);
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const orderAlertResponse = createAsyncThunk(
    'orderAlertResponse',
    async ({ alertId, responseId, index }, { rejectWithValue }) => {
        try {
            await putReq(`/alerts/${alertId}/responses/${responseId}/order`, {
                index,
            });
            return { id: responseId, order: index };
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

const initialState = {
    data: [],
};

const alertsSlice = createSlice({
    name: 'alerts',
    initialState,
    reducers: {
        addResponse: (state, { payload }) => {
            state.data = state.data.map((item) => {
                if (item.id === payload) {
                    item.responsesLeft -= 1;
                    if (item.responsesLeft === 0) {
                        item.status = 'inactive';
                        item.deadline = new Date().toISOString();
                    }
                    return item;
                }
                return item;
            });
            if (state.alert) {
                state.alert.responsesLeft -= 1;
                if (state.alert.responsesLeft === 0) {
                    state.alert.status = 'inactive';
                    state.alert.deadline = new Date().toISOString();
                }
            }
        },

        // The cached alert page names no journalist; a member allowed to see one gets it here.
        revealAlertAuthor: (state, { payload }) => {
            if (state.alert?.id === payload.id) {
                const {
                    author,
                    authorId,
                    profileId,
                    sourceUrl,
                    sourceLabel,
                    sourceBy,
                } = payload;
                state.alert = {
                    ...state.alert,
                    author,
                    authorId,
                    profileId,
                    sourceUrl,
                    sourceLabel,
                    sourceBy,
                };
            }
        },

        findResponse: (state, { payload }) => {
            state.alert = state.data.find((item) => item.id === payload);
        },
        reset: () => initialState,
    },
    // reducer to persist
    extraReducers(builder) {
        builder.addCase(hydrate, (state, action) => {
            return {
                ...state,
                ...action.payload.alerts,
            };
        });

        builder.addCase(fetchAlerts.fulfilled, (state, { payload }) => {
            state.data = payload;
            state.alert = null;
        });
        builder.addCase(fetchAlertBySlug.fulfilled, (state, { payload }) => {
            state.alert = payload;
        });
        builder.addCase(fetchResponses.fulfilled, (state, { payload }) => {
            state.alertResponses = payload;
        });
        builder.addCase(addAlert.fulfilled, (state, { payload }) => {
            state.alert = payload;
            state.data = [payload, ...state.data];
        });
        builder.addCase(updateAlert.fulfilled, (state, { payload }) => {
            state.alert = { ...state.alert, ...payload };
        });
        // A refused save (taken slug, source page off the profile site) leaves
        // the alert as it was; the page shows the error over the open editor.
        builder.addCase(updateAlert.rejected, (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        });
        builder.addCase(closeAlert.fulfilled, (state, { payload }) => {
            state.alert = { ...state.alert, ...payload };
        });
        builder.addCase(addAlertResponse.fulfilled, (state, { payload }) => {
            if (payload.isStorySource) {
                state.alertResponses.unshift(payload);
            } else {
                state.alertResponses.push(payload);
            }
        });
        builder.addCase(removeAlertResponse.fulfilled, (state, { payload }) => {
            state.alertResponses = state.alertResponses.filter(
                (item) => item.id !== payload
            );
        });
        builder.addCase(updateAlertResponse.fulfilled, (state, { payload }) => {
            state.alertResponses = state.alertResponses.map((item) => {
                if (item.id === payload.id) {
                    return payload;
                }
                return item;
            });
        });
        builder.addCase(removeAlert.fulfilled, (state, { payload }) => {
            state.alert = null;
        });
        builder.addCase(
            orderAlertResponse.fulfilled,
            (state, { payload: { id, order } }) => {
                const index = state.alertResponses.findIndex(
                    (item) => item.id === id
                );
                const [item] = state.alertResponses.splice(index, 1);

                if (order < 0) {
                    state.alertResponses.push({ ...item, index: null });
                } else {
                    // A pinned Star Quote goes directly below the Story Source, never above it.
                    const top =
                        !item.isStorySource &&
                        state.alertResponses[0]?.isStorySource
                            ? 1
                            : 0;
                    state.alertResponses.splice(top, 0, { ...item, index: 0 });
                }
            }
        );

        builder.addMatcher(
            isAnyOf(
                fetchAlerts.fulfilled,
                removeAlert.fulfilled,
                fetchAlertBySlug.fulfilled,
                fetchResponses.fulfilled,
                addAlert.fulfilled,
                closeAlert.fulfilled,
                addAlertResponse.fulfilled,
                removeAlertResponse.fulfilled,
                updateAlertResponse.fulfilled,
                orderAlertResponse.fulfilled,
                updateAlert.fulfilled
            ),
            (state) => {
                state.loading = false;
                state.error = null;
            }
        );
        builder.addMatcher(
            isAnyOf(
                fetchAlerts.pending,
                removeAlert.pending,
                fetchAlertBySlug.pending,
                fetchResponses.pending,
                addAlert.pending,
                closeAlert.pending,
                addAlertResponse.pending,
                removeAlertResponse.pending,
                updateAlertResponse.pending,
                orderAlertResponse.pending,
                updateAlert.pending
            ),
            (state) => {
                state.loading = true;
                state.error = null;
            }
        );
        builder.addMatcher(
            isAnyOf(
                fetchAlerts.rejected,
                fetchAlertBySlug.rejected,
                fetchResponses.rejected,
                addAlert.rejected,
                closeAlert.rejected,
                removeAlertResponse.rejected,
                updateAlertResponse.rejected,
                orderAlertResponse.rejected
            ),
            (state, { payload }) => {
                state.alert = null;
                state.loading = false;
                state.error = payload;
            }
        );
        // A refused response or delete leaves the alert on screen, so the page can say why.
        builder.addMatcher(
            isAnyOf(addAlertResponse.rejected, removeAlert.rejected),
            (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            }
        );
    },
});

export const { addResponse, reset, revealAlertAuthor } = alertsSlice.actions;

export const alertSelector = ({ alerts }) => alerts;

export default alertsSlice.reducer;
